/* =========================================================================
   LAZ Skincare - add a product to the routine without leaving it

   Owns the "Add product" toolbar button and the search panel it reveals, on
   templates/page.lsc-routine.liquid. Separate from lsc-routine-page.js: it
   reads and writes the routine only through window.LazRoutine, and it
   coordinates with the share panel only through data-routine-panel in the
   markup - opening a panel closes the others, and neither script knows the
   other exists.

   THREE REQUESTS, DELIBERATELY.

     1. /search/suggest.json while typing. Fast and platform-cached, and
        enough to render a result. Its `variants` array is ALWAYS empty and
        it carries no metafields, so it can say neither which strength a
        shopper wants nor which routine step a product belongs to.

     2. /products/<handle>.js for every result, BEFORE the list paints.
        ~3.5KB each, cached here by handle, and the only source of variants.
        Roughly half of this catalogue is multi-variant ("5% | 8% | 11% |
        15%", "180 ml | 60 ml Travel"), so a single Add button would quietly
        file Shopify's default variant under the shopper's routine. Fetching
        first means no wrong one-button row is ever clickable.

     3. The routine row section, on the click of Add, for the chosen product
        only. /products/<handle>?section_id=lsc-routine-row carries
        data-steps and data-actives, the source of the step and the
        interaction warnings. Per keystroke per result that would fetch
        fields nothing displays; at click time it is one request on a
        committed action, and R.fetchRows caches it so the routine renders
        that same row rather than fetching twice.

   Membership needs no round trip: suggest.json returns the numeric product
   id and the .js payload the numeric variant ids, so the gids are built
   locally and R.hasProduct() / R.hasVariant() answer immediately.
   ========================================================================= */

(function () {
  "use strict";

  const R = window.LazRoutine;
  if (!R) return;

  const root = document.querySelector("[data-lsc-routine-page]");
  if (!root) return;

  /* Shared views strip every data-mode-own node before this runs, so the
     button and the panel are simply absent there and this exits. */
  const toggle = root.querySelector("[data-routine-add-product]");
  const panel = root.querySelector("[data-routine-search-panel]");
  const input = root.querySelector("[data-routine-search-input]");
  const statusEl = root.querySelector("[data-routine-search-status]");
  const resultsEl = root.querySelector("[data-routine-search-results]");
  const closeBtn = root.querySelector("[data-routine-search-close]");
  if (!toggle || !panel || !input || !resultsEl) return;

  const LIMIT = 6;

  /* Long enough that a typed word is one request rather than six, short
     enough that the list feels like it is keeping up. */
  const DEBOUNCE_MS = 250;

  /* One character matches most of the catalogue and tells the shopper
     nothing. */
  const MIN_QUERY = 2;

  /* Money, without hardcoding a symbol: Shopify.currency names the shop's
     currency and the browser formats it for the visitor's locale. Missing
     currency means no price shown - a wrong currency is worse than none.

     Two formatters, because the two endpoints disagree: suggest.json returns
     a decimal string ("84.00") and /products/<handle>.js returns cents
     (8400). Mixing them up is a 100x price on screen. */
  const currency =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "";

  let formatMoney = () => "";
  if (currency) {
    try {
      const fmt = new Intl.NumberFormat(undefined, { style: "currency", currency });
      formatMoney = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? fmt.format(n) : "";
      };
    } catch {
      formatMoney = () => "";
    }
  }

  const formatCents = (cents) => {
    const n = Number(cents);
    return Number.isFinite(n) ? formatMoney(n / 100) : "";
  };

  /* ---------------------------------------------------------------------
     panel
     --------------------------------------------------------------------- */

  function openPanel() {
    root.querySelectorAll("[data-routine-panel]").forEach((node) => {
      if (node !== panel) node.hidden = true;
    });
    panel.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    input.focus();
  }

  /* restoreFocus is for the two deliberate closes - Escape and the x - where
     dropping focus at the top of the document would lose the shopper's
     place. A close caused by another panel opening must NOT pull focus away
     from whatever they just clicked. */
  /* LSC - Closing the panel now ends the search as well as hiding it.

     It used to hide the panel and nothing else: a debounce timer could still
     fire, an in-flight suggest request still resolved, and both rendered into
     a hidden list and rewrote the status line that a screen reader announces.
     Bumping queryId invalidates anything already in flight, including the
     per-row hydrations. */
  function closePanel(options) {
    window.clearTimeout(debounceTimer);
    queryId += 1;
    if (searchAbort) {
      searchAbort.abort();
      searchAbort = null;
    }

    panel.hidden = true;
    toggle.setAttribute("aria-expanded", "false");
    if (options && options.restoreFocus) toggle.focus();
  }

  toggle.addEventListener("click", () => {
    if (panel.hidden) openPanel();
    else closePanel();
  });

  if (closeBtn) {
    closeBtn.addEventListener("click", () => closePanel({ restoreFocus: true }));
  }

  panel.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    closePanel({ restoreFocus: true });
  });

  /* ---------------------------------------------------------------------
     searching
     --------------------------------------------------------------------- */

  let debounceTimer = null;

  /* Every request carries a sequence number and only the newest may render.
     Without it a slow request for "ret" can land after a fast one for
     "retinol" and leave the list showing a query the field no longer
     contains. */
  let queryId = 0;
  /* Aborts the suggest request of a superseded query — see runSearch. */
  let searchAbort = null;

  function setStatus(message) {
    if (statusEl) statusEl.textContent = message || "";
  }

  /* One promise per handle, kept for the life of the page, so re-typing a
     query costs nothing. A failed fetch resolves to null - "variants
     unknown" - rather than rejecting, so one bad product cannot blank the
     whole list. */
  const variantCache = new Map();

  function fetchVariants(handle) {
    if (variantCache.has(handle)) return variantCache.get(handle);

    const promise = fetch("/products/" + encodeURIComponent(handle) + ".js", {
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("product json unavailable");
        return res.json();
      })
      .then((body) => (body && Array.isArray(body.variants) ? body.variants : null))
      /* LSC - A failure is no longer remembered. The null was cached for the
         life of the page, so one dropped request permanently demoted that
         product to the single-button path — which is the "adds the wrong
         strength" bug this whole variant list exists to prevent. Forgetting it
         means the next search for the same product tries again. */
      .catch(() => {
        variantCache.delete(handle);
        return null;
      });

    variantCache.set(handle, promise);
    return promise;
  }

  /* LSC - Results paint after ONE round trip, then fill themselves in.

     This used to await Promise.all over /products/<handle>.js for every result
     before painting anything. Each of those is the entire product — every
     variant, every image, the full description — so six results is six large
     payloads, and the shopper watched "Searching…" for the slowest of them.
     Typing ten characters on a phone came to roughly 24 requests and several
     hundred KB before a single row appeared.

     Now suggest.json paints the rows immediately and each row hydrates when
     its own variants land. The rule that made the old shape necessary still
     holds — a product-level button must never be clickable before we know
     whether it has options to choose between, or it would quietly add the
     wrong one — so a row starts with a disabled button and swaps itself for
     the real one when it knows.

     Only the suggest request is aborted when a newer query supersedes this
     one. The product fetches are deliberately left running: they are cached
     by handle and shared across queries, and someone typing "retinol" one
     letter at a time keeps hitting the same products. */
  async function runSearch(term) {
    const id = ++queryId;

    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();
    const signal = searchAbort.signal;

    const params = new URLSearchParams();
    params.set("q", term);
    params.set("resources[type]", "product");
    params.set("resources[limit]", String(LIMIT));
    params.set("resources[options][unavailable_products]", "last");

    try {
      const res = await fetch("/search/suggest.json?" + params.toString(), {
        headers: { Accept: "application/json" },
        signal,
      });
      if (!res.ok) throw new Error("Search is unavailable right now.");

      const body = await res.json();
      if (id !== queryId) return; // a newer query has already been sent

      const products =
        (body.resources && body.resources.results && body.resources.results.products) || [];

      if (!products.length) {
        renderResults([], term);
        return;
      }

      /* `variants: undefined` is the pending state — buildResult draws the row
         with a disabled button and no options list. */
      renderResults(
        products.map((product) => ({ product, variants: undefined })),
        term
      );

      products.forEach((product, i) => {
        fetchVariants(product.handle).then((variants) => {
          if (id !== queryId) return; // results have moved on
          hydrateResult(i, { product, variants });
        });
      });
    } catch (err) {
      if (err && err.name === "AbortError") return;
      if (id !== queryId) return;
      resultsEl.textContent = "";
      setStatus(err.message || "Search is unavailable right now.");
    }
  }

  /* Replace one painted row with its finished self. Rebuilding the whole <li>
     rather than patching it keeps a single construction path — buildResult is
     the only thing that decides whether a product shows one button or a list
     of strengths. */
  function hydrateResult(index, entry) {
    const current = resultsEl.children[index];
    if (!current) return;
    current.replaceWith(buildResult(entry));
  }

  input.addEventListener("input", () => {
    window.clearTimeout(debounceTimer);
    const term = input.value.trim();

    if (term.length < MIN_QUERY) {
      queryId++; // abandon anything in flight
      resultsEl.textContent = "";
      setStatus(term.length ? "Keep typing…" : "");
      return;
    }

    setStatus("Searching…");
    debounceTimer = window.setTimeout(() => runSearch(term), DEBOUNCE_MS);
  });

  /* ---------------------------------------------------------------------
     results
     --------------------------------------------------------------------- */

  const productGidOf = (product) => "gid://shopify/Product/" + product.id;
  const variantGidOf = (variant) => "gid://shopify/ProductVariant/" + variant.id;

  /* One repaint function per rendered Add button, so a change to the routine
     refreshes their states in place. Rebuilding the list instead would throw
     away the shopper's scroll position and their place in the results. The
     array is emptied on every render, which is what drops the references to
     buttons that no longer exist. */
  const repainters = [];

  function priceLabel(product) {
    const min = formatMoney(product.price_min || product.price);
    if (!min) return "";
    const max = formatMoney(product.price_max);
    return max && max !== min ? "From " + min : min;
  }

  function renderResults(entries, term) {
    resultsEl.textContent = "";
    repainters.length = 0;

    if (!entries.length) {
      setStatus("No products match “" + term + "”.");
      return;
    }

    setStatus(entries.length + (entries.length === 1 ? " product" : " products") + " found.");
    entries.forEach((entry) => resultsEl.appendChild(buildResult(entry)));
  }

  function buildResult(entry) {
    const product = entry.product;
    const variants = entry.variants;

    /* A single-variant product has nothing to choose, so it keeps the one
       product-level button. Unknown variants (the .js fetch failed) take the
       same path: the row section resolves the variant at click time, which is
       what this did before variants were listed at all. */
    const options = Array.isArray(variants) && variants.length > 1 ? variants : null;

    const li = R.el("li", "lsc-routine-search__result");
    const row = R.el("div", "lsc-routine-search__row");

    /* Thumbnail. suggest.json gives a CDN URL; the width parameter keeps the
       browser from pulling a 2000px product shot for a 48px square. */
    const figure = R.el("a", "lsc-routine-search__media");
    figure.href = product.url;
    figure.tabIndex = -1;
    figure.setAttribute("aria-hidden", "true");

    const imageUrl = product.featured_image && product.featured_image.url;
    if (imageUrl) {
      const img = document.createElement("img");
      img.src = imageUrl + (imageUrl.includes("?") ? "&" : "?") + "width=120";
      img.alt = "";
      img.width = 48;
      img.height = 48;
      img.loading = "lazy";
      figure.appendChild(img);
    }
    row.appendChild(figure);

    const info = R.el("div", "lsc-routine-search__info");
    if (product.vendor) {
      info.appendChild(
        R.el("p", "lsc-routine-search__vendor text-xs text-opacity", product.vendor)
      );
    }

    const title = R.el("a", "lsc-routine-search__name", product.title);
    title.href = product.url;
    info.appendChild(title);

    const price = priceLabel(product);
    const meta = [];
    if (price) meta.push(price);
    if (product.available === false) meta.push("Currently unavailable");
    if (options) meta.push(options.length + " options");
    if (meta.length) {
      info.appendChild(
        R.el("p", "lsc-routine-search__meta text-xs text-opacity", meta.join(" · "))
      );
    }
    row.appendChild(info);

    /* Either the product gets one button, or its variants each get one.
       Never both: two ways to add the same product, one of which quietly
       picks for you, is the bug this feature exists to fix.

       LSC - `variants === undefined` means the product's options are still on
       their way. The row paints now with a button that holds the space and
       cannot be pressed, and hydrateResult() swaps the whole row when the
       answer arrives. Distinguished from `null`, which means the fetch failed
       and the row genuinely falls back to one button. */
    if (variants === undefined) row.appendChild(pendingAddButton(product));
    else if (!options) row.appendChild(buildAddButton(product, variants, null));
    li.appendChild(row);

    if (options) li.appendChild(buildVariantList(product, options));
    return li;
  }

  /* LSC - The placeholder in a row that has painted but does not yet know
     whether the product has strengths to choose between.

     Same class and therefore the same 44px circle, so the row does not change
     size when it hydrates — no reflow under the shopper's finger. Disabled,
     dimmed by .is-adding, and named for what it is doing, because a screen
     reader meeting a disabled unlabelled button learns nothing. */
  function pendingAddButton(product) {
    const button = R.el("button", "lsc-routine-search__add lsc-btn--act button button--secondary is-adding");
    button.type = "button";
    button.disabled = true;
    button.setAttribute("aria-label", "Checking options for " + product.title);

    const fill = R.el("span", "btn-fill");
    fill.setAttribute("data-fill", "");
    const text = R.el("span", "btn-text");
    text.appendChild(glyph(PLUS_GLYPH));
    button.append(fill, text);

    return button;
  }

  /* A real list, so a screen reader announces how many strengths there are
     and the group is labelled by the product it belongs to. */
  function buildVariantList(product, options) {
    const ul = R.el("ul", "lsc-routine-search__variants");
    ul.setAttribute("role", "list");
    ul.setAttribute("aria-label", "Options for " + product.title);
    options.forEach((variant) => ul.appendChild(buildVariantRow(product, variant)));
    return ul;
  }

  function buildVariantRow(product, variant) {
    const li = R.el("li", "lsc-routine-search__variant");

    li.appendChild(
      R.el("span", "lsc-routine-search__variant-name", variant.title || product.title)
    );

    /* Sold out is worth saying but not worth blocking on: a routine is a
       plan, and a shopper may well keep a product in it while it restocks.
       Add all to cart is where availability actually matters. */
    const bits = [];
    const money = formatCents(variant.price);
    if (money) bits.push(money);
    if (variant.available === false) bits.push("Sold out");
    if (bits.length) {
      li.appendChild(
        R.el(
          "span",
          "lsc-routine-search__variant-price text-xs text-opacity",
          bits.join(" · ")
        )
      );
    }

    li.appendChild(buildAddButton(product, null, variant));
    return li;
  }

  /* LSC - A plus. The add buttons in this panel carry no words any more — one
     glyph, one size, for a product row and for a strength inside it — so the
     mark has to say "add" on its own, and a plus is the one that does.

     It replaced a checklist mark that matched the header nav and the
     product-page control. That resemblance is worth less here than legibility
     at 18px with nothing beside it. */
  const PLUS_GLYPH = '<path d="M12 5v14M5 12h14"/>';

  const TICK_GLYPH = '<path d="M20 6L9 17l-5-5"/>';

  function glyph(paths) {
    const span = R.el("span", "lsc-routine-search__icon");
    span.setAttribute("aria-hidden", "true");
    span.innerHTML =
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      paths +
      "</svg>";
    return span;
  }

  /* One builder for both shapes.

       variant null     the product-level button. State comes from
                        R.hasProduct, and the variant is resolved from the
                        row section at click time (or from `variants` when
                        the product has exactly one).
       variant given    a per-strength button inside the variants list. State
                        comes from R.hasVariant, and that exact variant is
                        what gets stored. */
  function buildAddButton(product, variants, variant) {
    const productGid = productGidOf(product);
    const variantGid = variant ? variantGidOf(variant) : null;

    /* LSC - Built with the `is` option, not with setAttribute afterwards.

       A customized built-in is only upgraded at creation: setting the
       attribute on an existing element gives you the attribute and not the
       upgraded element, so this button was never actually a hover-button —
       the theme's fill animation was CSS-only on it while the identical
       control on the saved-routines page got the real thing. That file's own
       comment has said so since it was written; this one had drifted.

       try/catch because a browser without customized built-ins throws on the
       options form, and the fill is CSS either way. */
    let button;
    try {
      button = document.createElement("button", { is: "hover-button" });
    } catch {
      button = document.createElement("button");
    }
    button.className = "lsc-routine-search__add lsc-btn--act button button--secondary";
    button.type = "button";
    button.setAttribute("is", "hover-button");

    /* The variant class is layout only now — where the button sits in its row.
       It used to carry a smaller size as well; both shapes are one size. The
       accessible name is set by paint(), because with no visible text the name
       is the ONLY thing that says what state the button is in. */
    if (variant) button.classList.add("lsc-routine-search__add--variant");

    const fill = R.el("span", "btn-fill");
    fill.setAttribute("data-fill", "");
    const text = R.el("span", "btn-text");
    button.append(fill, text);

    /* Four states, two of them click targets:
         in      this exact thing is in the routine - says so, and stays
                 disabled rather than offering a remove. Removing belongs on
                 the routine itself, where the shopper can see what the gap
                 would be.
         switch  a DIFFERENT variant of this product is in the routine. R.add
                 replaces it, because a routine is a sequence of steps and
                 not a shopping list - so the button says so instead of
                 pretending to add a second strength.
         adding  in flight.
         add     the resting state. */
    /* LSC - The words are gone; the state now shows as a plus or a tick, and
       is SAID by the accessible name. That name is set here rather than once at
       build time, because it is the only channel left that distinguishes "add"
       from "switch" from "already in there" — a glyph cannot be read aloud and
       a disabled plus looks much like an enabled one.

       title as well as aria-label: on a desktop, an icon with no words needs a
       hover tooltip to be discoverable, and this is a list of otherwise
       identical buttons. */
    function paint(state) {
      text.textContent = "";
      button.classList.toggle("is-in-routine", state === "in");
      button.classList.toggle("is-adding", state === "adding");

      const name = variant ? product.title + " — " + variant.title : product.title;
      let label;

      if (state === "in") {
        text.append(glyph(TICK_GLYPH));
        label = name + " is in your routine";
        button.disabled = true;
      } else if (state === "adding") {
        text.append(glyph(PLUS_GLYPH));
        label = "Adding " + name + " to your routine";
        button.disabled = true;
      } else if (state === "switch") {
        text.append(glyph(PLUS_GLYPH));
        label = "Switch your routine to " + name;
        button.disabled = false;
      } else {
        text.append(glyph(PLUS_GLYPH));
        label = "Add " + name + " to routine";
        button.disabled = false;
      }

      button.setAttribute("aria-label", label);
      button.title = label;
    }

    function currentState() {
      if (!variantGid) return R.hasProduct(productGid) ? "in" : "add";
      if (R.hasVariant(variantGid)) return "in";
      return R.hasProduct(productGid) ? "switch" : "add";
    }

    /* An explicit flag rather than reading button.disabled, which cannot
       tell "mid-add" from "already in the routine". */
    let busy = false;

    paint(currentState());
    repainters.push(() => {
      if (!busy) paint(currentState());
    });

    button.addEventListener("click", async () => {
      busy = true;
      paint("adding");
      try {
        await addToRoutine(product, variantGid || knownSingleGid(variants), variant);
        paint("in");
      } catch (err) {
        R.toast(err.message, "error");
        paint(currentState());
      } finally {
        busy = false;
      }
    });

    return button;
  }

  /* A one-variant product already told us its variant id, so there is no
     reason to make the row section decide. Anything else returns null and
     lets the row decide, as before. */
  function knownSingleGid(variants) {
    return Array.isArray(variants) && variants.length === 1 ? variantGidOf(variants[0]) : null;
  }

  /* ---------------------------------------------------------------------
     adding
     --------------------------------------------------------------------- */

  /* The search result alone is not enough to file a product under a step. The
     routine row section is, and fetching it here means the step and the
     actives are resolved from the same markup the routine itself renders -
     one definition of what a product is, not two.

     The chosen variant goes into fetchRows so the cached row is the row for
     THAT variant: the routine renders from this same cache, and a row
     fetched for the default variant would show the wrong name and price. */
  async function addToRoutine(product, variantGid, variant) {
    const request = { handle: product.handle };
    if (variantGid) request.variant_gid = variantGid;

    const [entry] = await R.fetchRows([request]);
    const node = entry && entry.node;

    if (!node || !node.dataset.variantGid) {
      throw new Error(
        "We couldn't add " + product.title + ". Open its page and add it there."
      );
    }

    /* Step and actives are product-level metafields, so they are the same
       whichever variant was chosen - but the variant gid must be the chosen
       one, not whatever the section defaulted to. */
    const ok = R.add({
      variant_gid: variantGid || node.dataset.variantGid,
      product_gid: node.dataset.productGid || productGidOf(product),
      handle: node.dataset.handle || product.handle,
      step_label: R.firstStep(node),
      actives: R.activesOf(node),
    });

    if (!ok) {
      throw new Error("We couldn't save your routine - your browser is blocking storage.");
    }

    const name = node.dataset.title || product.title;
    R.toast(
      variant ? "Added " + name + " — " + variant.title + "." : "Added " + name + ".",
      "success"
    );
  }

  /* The routine can change from the routine itself (a remove), from another
     one of these buttons (a switch retires a sibling), or from another tab
     while this panel sits open - so the buttons re-read membership rather
     than trusting what they last painted. */
  R.onChange(() => repainters.forEach((repaint) => repaint()));
})();
